// 中文標籤 printing — the Taiwan compliance sticker that goes ON an imported
// cosmetic (MdoC), NOT the NOKE barcode hang-tag (see nokeLabelPrint.ts).
//
// Two label stocks: 45x60mm for everything, 50x80mm for the End Stick BB.
// Both carry the same mandatory blocks (化粧品衛生安全管理法 §7); the taller
// stock just gets more room, so nothing is dropped by size — only the type
// scales.
//
// The hard problem is the 全成分 list: a full INCI translation runs 300–700
// characters and has to fit a 39mm measure. So the document ships with a
// shrink-to-fit pass that measures the real rendered height on load and steps
// the body type down until the content clears the label box. It runs
// SYNCHRONOUSLY before window.print(), so what prints is what was measured.
//
// buildNokeCnLabelsDocumentHtml() is pure so a preview and the print popup come
// from one code path and cannot drift.

// ---- Label stock -----------------------------------------------------------

export type CnLabelSize = '45x60' | '50x80';

export const CN_LABEL_SIZES: Record<CnLabelSize, { width: number; height: number; label: string }> = {
  '45x60': { width: 45, height: 60, label: '45 × 60 mm (標準)' },
  '50x80': { width: 50, height: 80, label: '50 × 80 mm (End Stick BB)' },
};

export const DEFAULT_CN_LABEL_SIZE: CnLabelSize = '45x60';

export const isCnLabelSize = (v: string | null | undefined): v is CnLabelSize =>
  v === '45x60' || v === '50x80';

// ---- Sheet stock (gang printing) -------------------------------------------

// 'roll' = one sticker per page, fed from die-cut roll stock (the original mode).
// '100x120' = one large 10x12cm piece carrying several stickers, cut apart by
// hand afterwards. The piece is sized so the standard 45x60 tiles 2x2 into it
// with nothing left over vertically (2 x 60 = 120 exactly).
export type CnSheetMode = 'roll' | '100x120';

export const CN_SHEET_MODES: Record<
  CnSheetMode,
  { width: number | null; height: number | null; label: string }
> = {
  roll: { width: null, height: null, label: '單張（一頁一張，捲筒標籤）' },
  '100x120': { width: 100, height: 120, label: '10 × 12 cm 大張（一頁多張，自行裁切）' },
};

export const DEFAULT_CN_SHEET_MODE: CnSheetMode = 'roll';

export const isCnSheetMode = (v: string | null | undefined): v is CnSheetMode =>
  v === 'roll' || v === '100x120';

export const CN_SHEET_KEY = 'nokeCnLabelSheet';

export function readCnSheetMode(): CnSheetMode {
  const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(CN_SHEET_KEY) : null;
  return isCnSheetMode(raw) ? raw : DEFAULT_CN_SHEET_MODE;
}

export interface CnSheetLayout {
  cols: number;
  rows: number;
  perSheet: number;
  /** mm offset of the tiled block inside the piece; the block is centred. */
  offsetX: number;
  offsetY: number;
}

// How many stickers of a given stock fit on a piece, and where the block sits.
//
// No minimum outer margin is imposed: 50x80 tiles 2-across into exactly 100mm,
// so demanding even 2mm of margin would drop it to one per piece and defeat the
// point. The stickers carry 3mm of internal padding, so a printer's unprintable
// edge eats white, not content.
export function cnSheetLayout(size: CnLabelSize, sheet: CnSheetMode): CnSheetLayout | null {
  const piece = CN_SHEET_MODES[sheet];
  if (!piece.width || !piece.height) return null;
  const stock = CN_LABEL_SIZES[size];
  const cols = Math.max(1, Math.floor(piece.width / stock.width));
  const rows = Math.max(1, Math.floor(piece.height / stock.height));
  return {
    cols,
    rows,
    perSheet: cols * rows,
    offsetX: (piece.width - cols * stock.width) / 2,
    offsetY: (piece.height - rows * stock.height) / 2,
  };
}

// localStorage keys for the 中文標籤 print-position calibration (mm). Separate
// from the hang-tag keys — different stock, different feed alignment.
export const CN_OFFSET_X_KEY = 'nokeCnLabelOffsetX';
export const CN_OFFSET_Y_KEY = 'nokeCnLabelOffsetY';

export function readCnLabelOffset(): { offsetX: number; offsetY: number } {
  const readNum = (key: string): number => {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
    const n = parseFloat(raw || '0');
    return isNaN(n) ? 0 : n;
  };
  return { offsetX: readNum(CN_OFFSET_X_KEY), offsetY: readNum(CN_OFFSET_Y_KEY) };
}

// ---- Data ------------------------------------------------------------------

// One 中文標籤's content. Mirrors the noke_cn_labels row (and PIF §4) verbatim.
export interface CnLabelData {
  sku: string;
  product_name: string;
  variant_name?: string | null;
  use_zh?: string | null;
  usage_zh?: string | null;
  net_content?: string | null;
  /** Row key for net_content — 淨重 / 淨含量 / 容量, per the source dossier. */
  net_label?: string | null;
  ingredients?: string | null;
  caution_zh?: string | null;
  manufacturer?: string | null;
  manufacturer_addr?: string | null;
  origin?: string | null;
  importer_name?: string | null;
  importer_addr?: string | null;
  importer_tel?: string | null;
  shelf_life?: string | null;
  /** 有效期限 — distinct from 保存期限. MdoC uses 保存期限+有效期限, never 製造日期. */
  expiry?: string | null;
  lot_no?: string | null;
  license_no?: string | null;
  label_size?: string | null;
  /** Number of stickers to print for this product (default 1). */
  copies?: number;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const has = (v: string | null | undefined): boolean => !!(v && String(v).trim());

// Multi-line fields (用法, 注意事項) are stored with real newlines; keep the
// line breaks but let long lines wrap naturally.
const multiline = (v: string): string => escapeHtml(v.trim()).replace(/\n/g, '<br/>');

// ---- Layout ----------------------------------------------------------------

// Starting body type in pt, by stock. The shrink-to-fit pass only ever reduces
// this, so it is deliberately set to the largest size that a *short* label
// (perfume: no INCI translation of note) can carry legibly.
const START_BODY_PT: Record<CnLabelSize, number> = {
  '45x60': 6.0,
  '50x80': 6.8,
};

// Floor for the shrink-to-fit pass. Below ~3.2pt the 成分 list stops being
// readable with a loupe, which is what an inspector uses — so instead of going
// smaller the label overflows visibly and the operator sees it needs the
// bigger stock or a trimmed ingredient list.
const MIN_BODY_PT = 3.2;

function labelHtml(label: CnLabelData, size: CnLabelSize): string {
  const stock = CN_LABEL_SIZES[size];
  const title = [label.product_name, label.variant_name].filter(has).map((s) => escapeHtml(String(s).trim()));

  // One field = bold key + value, rendered INLINE. Short fields are packed into
  // semantic groups that flow and share lines; only the genuinely long blocks
  // get a paragraph of their own.
  //
  // The earlier layout gave every field a fixed 6.4em key column, so a line
  // like 「淨含量 8 mL」 used a third of the measure and still burned a whole
  // line — nine of them did. Reclaiming that dead column is what pays for
  // larger type on the same stock, which is what makes the print legible.
  const field = (key: string, value: string | null | undefined): string =>
    has(value)
      ? `<span class="f"><span class="k">${escapeHtml(key)}</span>${multiline(String(value))}</span>`
      : '';

  // A group of inline fields on its own line(s). Empty groups vanish.
  const group = (...fields: string[]): string => {
    const kept = fields.filter(Boolean);
    return kept.length ? `<div class="grp">${kept.join('')}</div>` : '';
  };
  // A long free-text block: run-in bold key, then the text at full measure.
  const block = (key: string, value: string | null | undefined, cls = ''): string =>
    has(value)
      ? `<div class="blk ${cls}"><span class="k">${escapeHtml(key)}</span>${multiline(String(value))}</div>`
      : '';

  // Block order still follows the approved MdoC 中文標籤 copy (Magnus,
  // 2026-08-30): 用途 → 使用方法 → 注意事項 → 淨含量 → 製造商/地址 →
  // 進口商/地址/電話 → 原產地 → 保存期限 → 有效期限 → 批號 → 全成分.
  // The grouping below only changes which of them SHARE a line.
  const body = [
    group(field('用途', label.use_zh), field(has(label.net_label) ? String(label.net_label).trim() : '淨含量', label.net_content)),
    block('使用方法', label.usage_zh),
    block('注意事項', label.caution_zh),
    group(field('製造商', label.manufacturer), field('製造地址', label.manufacturer_addr)),
    group(field('進口商', label.importer_name), field('地址', label.importer_addr), field('服務電話', label.importer_tel)),
    group(
      field('原產地', label.origin),
      field('保存期限', label.shelf_life),
      field('有效期限', label.expiry),
      field('批號', label.lot_no),
      // 特定用途化粧品 (防曬 / 美白) only.
      field('許可證字號', label.license_no)
    ),
    block('全成分', label.ingredients, 'ingredients'),
  ].filter(Boolean).join('');

  return `
    <div class="label" data-size="${size}" style="--label-w:${stock.width}mm; --label-h:${stock.height}mm;">
      <div class="label-content">
        <div class="fit">
          <div class="head">
            <span class="title">${title.join(' ') || '&nbsp;'}</span>
            <span class="sku">${escapeHtml(label.sku)}</span>
          </div>
          <div class="rows">${body}</div>
        </div>
      </div>
    </div>`;
}

export interface BuildCnLabelsOptions {
  autoPrint?: boolean;
  offsetX?: number;
  offsetY?: number;
  /** Screen-only preview: keep the dashed guides and skip the print dialog. */
  preview?: boolean;
  /** Roll stock (one sticker per page) or a large piece carrying several. */
  sheet?: CnSheetMode;
}

// Cut guides for the gang sheet: a hairline along every cell boundary, running
// the full width/height of the piece so a ruler or guillotine can follow it.
//
// Ticks in the margin (the price-card approach) do not work here: 45x60 tiles to
// exactly 120mm tall, so the horizontal cut has NO margin to put a tick in. The
// line sits on the shared edge of two stickers and is removed by the cut itself.
function cutGuidesHtml(size: CnLabelSize, layout: CnSheetLayout): string {
  const stock = CN_LABEL_SIZES[size];
  const parts: string[] = [];
  for (let c = 0; c <= layout.cols; c++) {
    const x = layout.offsetX + c * stock.width;
    parts.push(`<div class="cut cut-v" style="left:${x}mm"></div>`);
  }
  for (let r = 0; r <= layout.rows; r++) {
    const y = layout.offsetY + r * stock.height;
    parts.push(`<div class="cut cut-h" style="top:${y}mm"></div>`);
  }
  return parts.join('');
}

// PURE document builder. Labels of DIFFERENT stock sizes may be mixed in one
// call — each label declares its own @page size via a per-size @page rule and
// its own --label-w/--label-h, so a 50x80 End Stick sticker and a 45x60 one can
// come out of the same batch without re-running the dialog.
//
// NOTE on mixed stock: a single print job can only feed ONE physical roll, so
// the UI groups by size before calling this. The CSS supports the mix anyway so
// a preview can show the whole batch at once.
export function buildNokeCnLabelsDocumentHtml(
  labels: CnLabelData[],
  options: BuildCnLabelsOptions = {}
): string {
  const {
    autoPrint = true,
    offsetX = 0,
    offsetY = 0,
    preview = false,
    sheet = 'roll',
  } = options;

  const expanded: CnLabelData[] = [];
  labels.forEach((l) => {
    const copies = Math.max(1, Math.floor(l.copies ?? 1));
    for (let i = 0; i < copies; i++) expanded.push(l);
  });

  // The @page size must match the stock actually being fed. When the batch is
  // homogeneous (the normal case) declare that stock; a mixed preview falls
  // back to the first label's stock.
  const firstSize: CnLabelSize = isCnLabelSize(expanded[0]?.label_size)
    ? (expanded[0]!.label_size as CnLabelSize)
    : DEFAULT_CN_LABEL_SIZE;
  const pageStock = CN_LABEL_SIZES[firstSize];

  // Gang mode tiles onto a large piece. It needs ONE stock per document to lay a
  // grid out, so it follows the batch's first size — the caller already splits
  // mixed selections with groupCnLabelsBySize(). Falls back to roll mode when
  // the piece cannot hold even one sticker of this stock.
  const layout = cnSheetLayout(firstSize, sheet);
  const piece = CN_SHEET_MODES[sheet];
  const ganged = !!(layout && piece.width && piece.height);

  const sizeOf = (l: CnLabelData): CnLabelSize =>
    isCnLabelSize(l.label_size) ? (l.label_size as CnLabelSize) : DEFAULT_CN_LABEL_SIZE;

  let labelsHtml: string;
  if (ganged) {
    const stock = CN_LABEL_SIZES[firstSize];
    const guides = cutGuidesHtml(firstSize, layout!);
    const pages: string[] = [];
    for (let start = 0; start < expanded.length; start += layout!.perSheet) {
      const chunk = expanded.slice(start, start + layout!.perSheet);
      const cells = chunk
        .map((l, i) => {
          const c = i % layout!.cols;
          const r = Math.floor(i / layout!.cols);
          const x = layout!.offsetX + c * stock.width;
          const y = layout!.offsetY + r * stock.height;
          return `<div class="cell" style="left:${x}mm; top:${y}mm;">${labelHtml(l, firstSize)}</div>`;
        })
        .join('');
      pages.push(`<div class="page">${cells}${guides}</div>`);
    }
    labelsHtml = pages.join('');
  } else {
    labelsHtml = expanded.map((l) => labelHtml(l, sizeOf(l))).join('');
  }

  return `<!DOCTYPE html>
<html lang="zh-Hant">
<head>
<meta charset="utf-8" />
<title>中文標籤列印</title>
<style>
  :root {
    /* Print-position calibration — nudges label CONTENT only, never page size. */
    --off-x: ${offsetX}mm;
    --off-y: ${offsetY}mm;
    --start-45x60: ${START_BODY_PT['45x60']}pt;
    --start-50x80: ${START_BODY_PT['50x80']}pt;
  }
  @page { size: ${ganged ? `${piece.width}mm ${piece.height}mm` : `${pageStock.width}mm ${pageStock.height}mm`}; margin: 0; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: "Microsoft JhengHei", "PingFang TC", "Noto Sans TC", Arial, sans-serif;
    background: #f5f5f5;
    /* Never let the browser thin CJK strokes at these sizes. */
    -webkit-font-smoothing: antialiased;
  }
  .toolbar { padding: 10px; text-align: center; background: #fff; border-bottom: 1px solid #ddd; }
  .toolbar button { font-size: 14px; padding: 8px 18px; margin: 0 4px; border: none; border-radius: 4px; cursor: pointer; color: #fff; }
  .btn-print { background: #4c5e8a; }
  .btn-close { background: #888; }
  .hint { font-size: 12px; color: #666; padding: 6px 10px 0; text-align: center; }
  .sheet { display: flex; flex-wrap: wrap; align-content: flex-start; gap: 2mm; padding: 2mm; }

${ganged ? `
  /* ---- gang sheet: one .page per piece of stock, cells absolutely placed ---- */
  .page {
    position: relative;
    width: ${piece.width}mm;
    height: ${piece.height}mm;
    background: #fff;
    overflow: hidden;
    page-break-after: always;
    break-after: page;
  }
  .page:last-child { page-break-after: auto; break-after: auto; }
  .page + .page { margin-top: 4mm; }   /* screen only, separates the pieces */
  .cell { position: absolute; }
  /* Hairline on every cell boundary. It lands ON the cut line, so the blade
     removes it — that is why it may sit over the sticker edge (3mm of padding
     there is white) instead of needing a gutter that the 45x60 tiling has none
     of. #999 because a #ccc hairline is invisible on a thermal print. */
  .cut { position: absolute; background: #999; pointer-events: none; }
  .cut-v { top: 0; width: 0.1mm; height: 100%; margin-left: -0.05mm; }
  .cut-h { left: 0; height: 0.1mm; width: 100%; margin-top: -0.05mm; }

  /* On a gang sheet the sticker really IS the full nominal height — the 2mm
     shim on .label exists only to absorb per-label page rounding on roll stock,
     and there is no per-label page here. The fit pass measures this taller box,
     so it prints exactly what it measured (and fits marginally more text). */
  .cell .label { height: var(--label-h); }
` : ''}
  .label {
    width: var(--label-w);
    /* 2mm shorter than the stock, in BOTH screen and print. Two reasons:
       (a) it absorbs the printer driver's page rounding — at 0.5mm a 45x60
           label spilled a blank second page out of every print;
       (b) the shrink-to-fit pass measures THIS box, so it has to be the same
           box that prints, or the fit guarantee is measured against the wrong
           height. 2mm is the clearance already proven on this printer by the
           hang-tag renderer (nokeLabelPrint.ts). */
    height: calc(var(--label-h) - 2mm);
    border: 1px dashed #ccc;  /* screen only */
    overflow: hidden;
    page-break-inside: avoid;
    background: #fff;
    color: #000;
  }
  .label-content {
    width: 100%;
    height: 100%;
    /* Same insets as the hang-tag, which is already calibrated on this printer;
       2mm all round sat the content ~1mm too high on the stock. */
    padding: 3mm 2.4mm 2.5mm;
    overflow: hidden;
    transform: translate(var(--off-x, 0), var(--off-y, 0));
  }
  /* .fit is what the shrink-to-fit pass scales; its font-size cascades to
     every block below, so one number controls the whole label. */
  .fit {
    font-size: var(--start-45x60);
    line-height: 1.28;
    display: flex;
    flex-direction: column;
  }
  .label[data-size="50x80"] .fit { font-size: var(--start-50x80); }

  /* 品名 and SKU share the first line — the SKU is a short internal code and
     does not deserve a line of its own on a 45mm measure. */
  /* Block, NOT flex: as a flex row the SKU sat on the first line's baseline and
     a wrapping 品名 stranded it mid-title (「…（滾珠  M11 / 瓶）」). Inline, it
     simply trails the last line of the name. */
  .head {
    padding-bottom: 0.5mm;
    margin-bottom: 0.8mm;
    border-bottom: 0.25mm solid #000;
  }
  .title { font-size: 1.35em; font-weight: 700; line-height: 1.2; }
  .sku { font-size: 0.9em; color: #444; margin-left: 1.2mm; white-space: nowrap; }

  .rows { display: flex; flex-direction: column; gap: 0.5mm; }

  /* A group of short fields, flowing inline and sharing lines. Deliberately
     NOT justified: justifying mixed CJK/Latin at ~4pt tore visible rivers
     through the addresses and INCI names. Ragged right is the tidier read. */
  .grp { }
  /* A long free-text block at full measure. */
  .blk { }

  /* Bold run-in key, then the value. The thin space after the key is what
     keeps it readable without a column. */
  .k { font-weight: 700; margin-right: 0.7mm; }
  .f { display: inline; }
  /* Separator between inline fields — a middle dot reads as a list break
     without the noise of a pipe, and costs less width than a comma+space. */
  .f + .f::before { content: "・"; font-weight: 400; color: #555; }

  .blk .k { margin-right: 0.8mm; }

  @media print {
    body { background: #fff; }
    .toolbar, .hint { display: none; }
    .sheet { display: block; gap: 0; padding: 0; }
    .label {
      border: none;
      /* height is NOT re-declared here — it is the same box the fit pass
         measured on screen (see .label above). */
      page-break-after: always;
      break-after: page;
    }
    .label:last-child { page-break-after: auto; break-after: auto; }${ganged ? `
    /* On a gang sheet the PAGE breaks, never the individual sticker — otherwise
       every label on the piece would eject its own blank page. */
    .cell .label { page-break-after: auto; break-after: auto; }
    /* \`.page + .page\` (0,2,0) outranks a bare \`.page\` here, so the screen gap
       must be cancelled with the same selector or it prints as a real 4mm
       offset on every piece after the first. */
    .page, .page + .page { box-shadow: none; margin: 0; }` : ''}
  }
</style>
</head>
<body>
  <div class="toolbar">
    <button class="btn-print" onclick="window.print()">列印</button>
    <button class="btn-close" onclick="window.close()">關閉</button>
  </div>
  ${preview ? '<div class="hint">預覽（虛線為標籤邊界，不會印出）</div>' : ''}
  <div class="sheet">${labelsHtml}</div>
<script>
  // Shrink-to-fit: step each label's body type down until its content clears
  // the label box. Runs synchronously before print() so the printed result is
  // exactly what was measured. Pure layout reads/writes, no async decode.
  (function () {
    var MIN_PT = ${MIN_BODY_PT};
    var STEP = 0.1;
    function fit(label) {
      var content = label.querySelector('.label-content');
      var box = label.querySelector('.fit');
      if (!content || !box) return;
      var pt = parseFloat(getComputedStyle(box).fontSize) * 72 / 96;
      // Available height inside the padding box.
      var cs = getComputedStyle(content);
      var avail = content.clientHeight
        - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
      var guard = 0;
      while (box.scrollHeight > avail && pt > MIN_PT && guard < 400) {
        pt -= STEP;
        box.style.fontSize = pt.toFixed(2) + 'pt';
        guard++;
      }
      // Flag anything that still does not fit at the legibility floor, so the
      // operator sees it on screen rather than discovering a cut-off 成分 list
      // after the roll is printed.
      if (box.scrollHeight > avail) {
        label.setAttribute('data-overflow', '1');
        label.style.outline = '1.5px solid #c62828';
      }
    }
    function fitAll() {
      var labels = document.querySelectorAll('.label');
      for (var i = 0; i < labels.length; i++) fit(labels[i]);
      var over = document.querySelectorAll('.label[data-overflow="1"]').length;
      if (over > 0) {
        var hint = document.createElement('div');
        hint.className = 'hint';
        hint.style.color = '#c62828';
        hint.textContent = over + ' 張標籤內容過長，已到最小字級仍放不下（紅框）'
          + '——請改用大尺寸標籤或精簡成分敘述。';
        document.body.insertBefore(hint, document.querySelector('.sheet'));
      }
    }
    fitAll();
    ${autoPrint
      ? `window.addEventListener('load', function () {
      // Re-fit after webfonts settle, then print.
      fitAll();
      setTimeout(function () { window.focus(); window.print(); }, 150);
    });`
      : `window.addEventListener('load', fitAll);`}
  })();
</script>
</body>
</html>`;
}

function openPrintWindow(html: string): void {
  const win = window.open('', '_blank', 'width=620,height=820');
  if (!win) {
    // eslint-disable-next-line no-alert
    alert('無法開啟列印視窗，請允許此網站的彈出視窗後再試。');
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
}

// Print one sticker per copy. Caller must pass labels of a SINGLE stock size —
// one print job feeds one roll. groupCnLabelsBySize() does that split.
export function printNokeCnLabels(labels: CnLabelData[], sheet?: CnSheetMode): void {
  if (labels.length === 0) return;
  const { offsetX, offsetY } = readCnLabelOffset();
  openPrintWindow(
    buildNokeCnLabelsDocumentHtml(labels, {
      autoPrint: true,
      offsetX,
      offsetY,
      sheet: sheet ?? readCnSheetMode(),
    })
  );
}

// On-screen proof before committing a roll — same builder, no print dialog.
export function previewNokeCnLabels(labels: CnLabelData[], sheet?: CnSheetMode): void {
  if (labels.length === 0) return;
  const { offsetX, offsetY } = readCnLabelOffset();
  openPrintWindow(
    buildNokeCnLabelsDocumentHtml(labels, {
      autoPrint: false,
      preview: true,
      offsetX,
      offsetY,
      sheet: sheet ?? readCnSheetMode(),
    })
  );
}

// Split a mixed selection into one batch per label stock, because a printer
// feeds one roll at a time. Returns entries in a stable size order.
export function groupCnLabelsBySize(labels: CnLabelData[]): Array<[CnLabelSize, CnLabelData[]]> {
  const buckets: Record<CnLabelSize, CnLabelData[]> = { '45x60': [], '50x80': [] };
  labels.forEach((l) => {
    const size: CnLabelSize = isCnLabelSize(l.label_size) ? (l.label_size as CnLabelSize) : DEFAULT_CN_LABEL_SIZE;
    buckets[size].push(l);
  });
  return (Object.keys(buckets) as CnLabelSize[])
    .filter((s) => buckets[s].length > 0)
    .map((s) => [s, buckets[s]] as [CnLabelSize, CnLabelData[]]);
}

// ---- Calibration -----------------------------------------------------------

// One full-bleed frame + crosshair at the given offset, on the given stock, so
// print alignment is visually obvious. Same page/offset CSS as real labels.
export function buildCnCalibrationDocumentHtml(
  size: CnLabelSize,
  offsetX: number,
  offsetY: number,
  options: { autoPrint?: boolean; sheet?: CnSheetMode } = {}
): string {
  const { autoPrint = true, sheet = 'roll' } = options;
  const stock = CN_LABEL_SIZES[size];
  const layout = cnSheetLayout(size, sheet);
  const piece = CN_SHEET_MODES[sheet];
  const ganged = !!(layout && piece.width && piece.height);

  // Gang mode must be calibrated on the piece it actually feeds: every cell gets
  // its own frame + crosshair, so a shift that only shows up in the far corner
  // (skew, not a uniform offset) is visible before a real batch is committed.
  const cell = `
      <div class="label-content">
        <div class="calib-frame"></div>
        <div class="cross-h"></div>
        <div class="cross-v"></div>
        <div class="calib-text">${size}　X:${offsetX} Y:${offsetY}</div>
      </div>`;

  const body = ganged
    ? `<div class="page">${Array.from({ length: layout!.perSheet }, (_, i) => {
        const c = i % layout!.cols;
        const r = Math.floor(i / layout!.cols);
        const x = layout!.offsetX + c * stock.width;
        const y = layout!.offsetY + r * stock.height;
        return `<div class="cell" style="left:${x}mm; top:${y}mm;"><div class="label">${cell}</div></div>`;
      }).join('')}${cutGuidesHtml(size, layout!)}</div>`
    : `<div class="label">${cell}</div>`;

  return `<!DOCTYPE html>
<html lang="zh-Hant">
<head>
<meta charset="utf-8" />
<title>中文標籤列印校正</title>
<style>
  :root { --label-w: ${stock.width}mm; --label-h: ${stock.height}mm; --off-x: ${offsetX}mm; --off-y: ${offsetY}mm; }
  @page { size: ${ganged ? `${piece.width}mm ${piece.height}mm` : `${stock.width}mm ${stock.height}mm`}; margin: 0; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, "Microsoft JhengHei", sans-serif; background: #f5f5f5; }
  .toolbar { padding: 10px; text-align: center; background: #fff; border-bottom: 1px solid #ddd; }
  .toolbar button { font-size: 14px; padding: 8px 18px; margin: 0 4px; border: none; border-radius: 4px; cursor: pointer; color: #fff; }
  .btn-print { background: #4c5e8a; }
  .btn-close { background: #888; }
  .label { width: var(--label-w); height: calc(var(--label-h) - 2mm); border: 1px dashed #ccc; overflow: hidden; background: #fff; color: #111; }${ganged ? `
  .page { position: relative; width: ${piece.width}mm; height: ${piece.height}mm; background: #fff; overflow: hidden; }
  .cell { position: absolute; }
  .cut { position: absolute; background: #999; }
  .cut-v { top: 0; width: 0.1mm; height: 100%; margin-left: -0.05mm; }
  .cut-h { left: 0; height: 0.1mm; width: 100%; margin-top: -0.05mm; }
  .cell .label { height: var(--label-h); }` : ''}
  .label-content { position: relative; width: 100%; height: 100%; transform: translate(var(--off-x, 0), var(--off-y, 0)); }
  .calib-frame { position: absolute; inset: 1mm; border: 0.3mm solid #000; }
  .cross-h { position: absolute; top: 50%; left: 0; width: 100%; height: 0; border-top: 0.2mm solid #000; }
  .cross-v { position: absolute; left: 50%; top: 0; height: 100%; width: 0; border-left: 0.2mm solid #000; }
  .calib-text { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); background: #fff; padding: 0.5mm 1mm; font-size: 9pt; font-weight: 700; }${ganged ? `
  /* the caption has to sit inside a 45mm cell without wrapping */
  .calib-text { font-size: 8pt; white-space: nowrap; }` : ''}
  @media print { body { background: #fff; } .toolbar { display: none; } .label { border: none; } }
</style>
</head>
<body>
  <div class="toolbar">
    <button class="btn-print" onclick="window.print()">列印</button>
    <button class="btn-close" onclick="window.close()">關閉</button>
  </div>
  ${body}
  ${autoPrint
    ? `<script>window.addEventListener('load', function () { setTimeout(function () { window.focus(); window.print(); }, 150); });</script>`
    : ''}
</body>
</html>`;
}

export function printCnCalibrationLabel(
  size: CnLabelSize,
  offsetX: number,
  offsetY: number,
  sheet?: CnSheetMode
): void {
  openPrintWindow(
    buildCnCalibrationDocumentHtml(size, offsetX, offsetY, {
      autoPrint: true,
      sheet: sheet ?? readCnSheetMode(),
    })
  );
}
